// client/src/components/analytics/SocialAnalytics.jsx
// Main social media analytics dashboard
// Displays KPIs: engagement rate, reach/impressions, CTR, follower growth, top posts

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchAnalyticsOverview,
  fetchPlatformAnalytics,
  fetchFollowerGrowth,
  fetchAnalyticsTrends,
  fetchTopPosts,
  fetchClickStats,
  formatMetricNumber,
  PLATFORM_META,
} from '../../services/analyticsApi';
import AppFooter from '../AppFooter';
import { FaArrowLeft, FaHome } from 'react-icons/fa';

const PERIOD_OPTIONS = [
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 30, label: '30 Days' },
  { value: 60, label: '60 Days' },
  { value: 90, label: '90 Days' },
];

function SocialAnalytics() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const tenantId = Number(tenant);

  const [days, setDays] = useState(30);
  const [platform, setPlatform] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [overview, setOverview] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [followers, setFollowers] = useState(null);
  const [trends, setTrends] = useState([]);
  const [topPosts, setTopPosts] = useState([]);
  const [clickStats, setClickStats] = useState(null);

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);

    try {
      const opts = { days, platform: platform || undefined };
      const [overviewRes, platformsRes, followersRes, trendsRes, topPostsRes, clicksRes] =
        await Promise.allSettled([
          fetchAnalyticsOverview(tenantId, opts),
          fetchPlatformAnalytics(tenantId, opts),
          fetchFollowerGrowth(tenantId, { days: Math.max(days, 30), platform: platform || undefined }),
          fetchAnalyticsTrends(tenantId, opts),
          fetchTopPosts(tenantId, { days, limit: 5, platform: platform || undefined }),
          fetchClickStats(tenantId, { days }),
        ]);

      if (overviewRes.status === 'fulfilled') setOverview(overviewRes.value.kpis);
      if (platformsRes.status === 'fulfilled') setPlatforms(platformsRes.value.platforms || []);
      if (followersRes.status === 'fulfilled') setFollowers(followersRes.value);
      if (trendsRes.status === 'fulfilled') setTrends(trendsRes.value.trends || []);
      if (topPostsRes.status === 'fulfilled') setTopPosts(topPostsRes.value.posts || []);
      if (clicksRes.status === 'fulfilled') setClickStats(clicksRes.value);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [tenantId, days, platform]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!tenantId) return <p style={{ color: 'red' }}>Invalid tenant</p>;

  return (
    <div style={{ paddingBottom: 80 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 600 }}>Social Analytics</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            className="form-select form-select-sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            {PERIOD_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            style={{ width: 'auto' }}
          >
            <option value="">All Platforms</option>
            {Object.entries(PLATFORM_META).map(([key, meta]) => (
              <option key={key} value={key}>{meta.name}</option>
            ))}
          </select>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={loadData}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button type="button" className="btn-close float-end" onClick={() => setError(null)}></button>
        </div>
      )}

      {loading && !overview ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading analytics...</p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <KPICards overview={overview} clickStats={clickStats} />

          {/* Engagement Trend */}
          {trends.length > 0 && <TrendChart trends={trends} />}

          {/* Platform Breakdown */}
          {platforms.length > 0 && <PlatformBreakdown platforms={platforms} />}

          {/* Follower Growth */}
          {followers?.current?.length > 0 && <FollowerGrowth data={followers} />}

          {/* Top Posts */}
          {topPosts.length > 0 && (
            <TopPostsList posts={topPosts} onViewAll={() => navigate(`/${tenant}/analytics/posts`)} />
          )}

          {/* Empty state */}
          {!overview?.postsTracked && !loading && (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-bar-chart" style={{ fontSize: '3rem' }}></i>
              <h4 className="mt-3">No Analytics Data Yet</h4>
              <p>Analytics data will appear here once your published posts have been tracked.</p>
              <p className="small">The analytics worker collects metrics hourly from your connected platforms.</p>
            </div>
          )}
        </>
      )}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(-1) },
          { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}

// ============================================================================
// KPI Cards
// ============================================================================
function KPICards({ overview, clickStats }) {
  if (!overview) return null;

  const cards = [
    {
      label: 'Engagement Rate',
      value: `${overview.engagementRate || 0}%`,
      sub: `${formatMetricNumber(overview.totalEngagement)} total engagements`,
      color: '#4CAF50',
      icon: 'bi-heart-fill',
    },
    {
      label: 'Reach',
      value: formatMetricNumber(overview.totalReach),
      sub: `${formatMetricNumber(overview.totalImpressions)} impressions`,
      color: '#2196F3',
      icon: 'bi-eye-fill',
    },
    {
      label: 'Click-Through Rate',
      value: `${overview.ctr || 0}%`,
      sub: `${formatMetricNumber(overview.totalClicks)} clicks`,
      color: '#FF9800',
      icon: 'bi-cursor-fill',
    },
    {
      label: 'Link Clicks',
      value: formatMetricNumber(clickStats?.totals?.totalClicks || 0),
      sub: `${clickStats?.totals?.uniqueLinks || 0} tracked links`,
      color: '#9C27B0',
      icon: 'bi-link-45deg',
    },
    {
      label: 'Posts Published',
      value: formatMetricNumber(overview.postsPublished),
      sub: `${overview.postsTracked} with metrics`,
      color: '#607D8B',
      icon: 'bi-send-fill',
    },
    {
      label: 'Video Views',
      value: formatMetricNumber(overview.totalVideoViews),
      sub: 'across all platforms',
      color: '#E91E63',
      icon: 'bi-play-circle-fill',
    },
  ];

  return (
    <div className="row g-3 mb-4">
      {cards.map((card, i) => (
        <div key={i} className="col-6 col-md-4 col-lg-2">
          <div className="card h-100" style={{ borderTop: `3px solid ${card.color}` }}>
            <div className="card-body p-2 p-md-3">
              <div className="d-flex align-items-center mb-1">
                <i className={`bi ${card.icon} me-1`} style={{ color: card.color, fontSize: '0.9rem' }}></i>
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>{card.label}</small>
              </div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{card.value}</div>
              <small className="text-muted" style={{ fontSize: '0.65rem' }}>{card.sub}</small>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Trend Chart (simple CSS bar chart - no external library needed)
// ============================================================================
function TrendChart({ trends }) {
  if (!trends.length) return null;

  const maxEngagement = Math.max(...trends.map(t => t.engagement), 1);
  const maxReach = Math.max(...trends.map(t => t.reach), 1);

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h6 className="mb-0">Engagement & Reach Trend</h6>
      </div>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 120, overflowX: 'auto' }}>
          {trends.map((t, i) => {
            const engHeight = Math.max((t.engagement / maxEngagement) * 100, 2);
            const reachHeight = Math.max((t.reach / maxReach) * 100, 2);
            const dateStr = new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 0 30px', minWidth: 30 }}>
                <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 100 }}>
                  <div
                    style={{ width: 8, height: `${engHeight}%`, backgroundColor: '#4CAF50', borderRadius: '2px 2px 0 0' }}
                    title={`Engagement: ${t.engagement}`}
                  ></div>
                  <div
                    style={{ width: 8, height: `${reachHeight}%`, backgroundColor: '#2196F3', borderRadius: '2px 2px 0 0', opacity: 0.6 }}
                    title={`Reach: ${t.reach}`}
                  ></div>
                </div>
                <small style={{ fontSize: '0.55rem', color: '#999', marginTop: 2, whiteSpace: 'nowrap' }}>
                  {dateStr}
                </small>
              </div>
            );
          })}
        </div>
        <div className="d-flex gap-3 mt-2">
          <small><span style={{ display: 'inline-block', width: 10, height: 10, backgroundColor: '#4CAF50', borderRadius: 2, marginRight: 4 }}></span>Engagement</small>
          <small><span style={{ display: 'inline-block', width: 10, height: 10, backgroundColor: '#2196F3', borderRadius: 2, marginRight: 4, opacity: 0.6 }}></span>Reach</small>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Platform Breakdown
// ============================================================================
function PlatformBreakdown({ platforms }) {
  return (
    <div className="card mb-4">
      <div className="card-header">
        <h6 className="mb-0">Performance by Platform</h6>
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th>Platform</th>
                <th className="text-end">Posts</th>
                <th className="text-end">Engagement</th>
                <th className="text-end">Eng. Rate</th>
                <th className="text-end">Reach</th>
                <th className="text-end">Impressions</th>
                <th className="text-end">CTR</th>
              </tr>
            </thead>
            <tbody>
              {platforms.map((p, i) => {
                const meta = PLATFORM_META[p.platform] || { name: p.platform, color: '#666', icon: 'bi-globe' };
                return (
                  <tr key={i}>
                    <td>
                      <i className={`bi ${meta.icon} me-1`} style={{ color: meta.color }}></i>
                      {meta.name}
                    </td>
                    <td className="text-end">{p.posts}</td>
                    <td className="text-end">{formatMetricNumber(p.engagement)}</td>
                    <td className="text-end">{p.engagementRate}%</td>
                    <td className="text-end">{formatMetricNumber(p.reach)}</td>
                    <td className="text-end">{formatMetricNumber(p.impressions)}</td>
                    <td className="text-end">{p.ctr}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Follower Growth
// ============================================================================
function FollowerGrowth({ data }) {
  if (!data?.current?.length) return null;

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h6 className="mb-0">Follower Growth</h6>
      </div>
      <div className="card-body">
        <div className="row g-3">
          {data.current.map((p, i) => {
            const meta = PLATFORM_META[p.platform] || { name: p.platform, color: '#666', icon: 'bi-globe' };
            const isPositive = p.growth >= 0;
            return (
              <div key={i} className="col-6 col-md-4 col-lg-3">
                <div className="card h-100">
                  <div className="card-body p-2 text-center">
                    <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: '1.2rem' }}></i>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: 4 }}>
                      {formatMetricNumber(p.currentFollowers)}
                    </div>
                    <small className="text-muted d-block">{meta.name}</small>
                    <small style={{ color: isPositive ? '#4CAF50' : '#f44336', fontWeight: 600 }}>
                      {isPositive ? '+' : ''}{formatMetricNumber(p.growth)} ({p.growthRate}%)
                    </small>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Top Posts List
// ============================================================================
function TopPostsList({ posts, onViewAll }) {
  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Top Posts by Engagement</h6>
        {onViewAll && (
          <button className="btn btn-sm btn-outline-primary" onClick={onViewAll}>
            View All
          </button>
        )}
      </div>
      <div className="list-group list-group-flush">
        {posts.map((post, i) => {
          const meta = PLATFORM_META[post.metricPlatform] || { name: post.metricPlatform, color: '#666', icon: 'bi-globe' };
          return (
            <div key={i} className="list-group-item">
              <div className="d-flex justify-content-between align-items-start">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="d-flex align-items-center gap-1 mb-1">
                    <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: '0.8rem' }}></i>
                    <small className="text-muted">
                      {post.postedOn ? new Date(post.postedOn).toLocaleDateString() : ''}
                    </small>
                  </div>
                  <p className="mb-1" style={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {post.content || '(No text content)'}
                  </p>
                  <div className="d-flex gap-3 flex-wrap">
                    <small className="text-muted">
                      <i className="bi bi-heart-fill text-danger me-1"></i>{formatMetricNumber(post.likes)}
                    </small>
                    <small className="text-muted">
                      <i className="bi bi-chat-fill me-1"></i>{formatMetricNumber(post.comments)}
                    </small>
                    <small className="text-muted">
                      <i className="bi bi-share-fill me-1"></i>{formatMetricNumber(post.shares)}
                    </small>
                    {post.reach > 0 && (
                      <small className="text-muted">
                        <i className="bi bi-eye-fill me-1"></i>{formatMetricNumber(post.reach)} reach
                      </small>
                    )}
                  </div>
                </div>
                <div className="text-end ms-2">
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#4CAF50' }}>
                    {formatMetricNumber(post.engagement)}
                  </div>
                  <small className="text-muted">{post.engagementRate}%</small>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SocialAnalytics;
